import { useRef, useState } from "react";
import type { DateRange } from "react-day-picker";

import { LogicalFilter } from "@refinedev/core";
import { UseTableReturnType } from "@refinedev/react-table";
import type { Column } from "@tanstack/react-table";

import { DatePickerWithRange } from "@/components/fields/date-picker";
import { RemoteSelect } from "@/components/fields/remote-select";
import { Label } from "@/components/ui/label";
import { parseUnixTimestamp, zonedStartOfDayUnix } from "@/lib/dateUtils";
import { useGeodataRepository } from "@/pages/geodata/hooks/use-geodata-repository";
import {
  GeodataEditFormType,
  geodataEditSchema,
} from "@/pages/geodata/schemas/geodata.edit.form";

type DataTableDateFilterProps<TData, TValue> = {
  column: Column<TData, TValue>;
  table: UseTableReturnType<any>;
};

/**
 * Date column filter with an optional geodata-driven timezone anchor.
 *
 * The operator can search geodata and pick a place; its `timezone` then defines
 * the day boundaries sent to the backend, so a date range lines up with the
 * timezone in which the rows are displayed rather than the operator's own. With
 * no place selected the boundaries fall back to the browser's local timezone.
 *
 * Both the range and the timezone feed `apply()` through refs rather than the
 * render closure. This makes the result independent of selection order and
 * immune to `RemoteSelect`'s memoised `onValueChanged` (it closes over the first
 * render): picking the place after the dates still re-applies with the current
 * range, and picking dates after the place still uses the current timezone.
 */
export function DataTableDateFilter<TData, TValue>({
  column,
  table,
}: DataTableDateFilterProps<TData, TValue>) {
  const { search } = useGeodataRepository();

  const findValue = (operator: "gte" | "lt") =>
    table.refineCore.filters.find(
      (f) =>
        (f as LogicalFilter).field === column.id && f.operator === operator,
    )?.value;

  const initialRange: DateRange | undefined = (() => {
    const from = findValue("gte");
    const to = findValue("lt");
    if (!from && !to) return undefined;
    return {
      from: from ? parseUnixTimestamp(Number(from)).toDate() : undefined,
      to: to ? parseUnixTimestamp(Number(to)).toDate() : undefined,
    };
  })();

  const [geodata, setGeodata] = useState<GeodataEditFormType | undefined>(
    undefined,
  );

  // Source of truth for apply(): refs so callbacks always read current values,
  // regardless of render-closure staleness or the order the operator picks in.
  const rangeRef = useRef<DateRange | undefined>(initialRange);
  const timezoneRef = useRef<string | undefined>(undefined);

  const apply = () => {
    const range = rangeRef.current;
    const timezone = timezoneRef.current;
    const filters: LogicalFilter[] = [];
    if (range?.from) {
      filters.push({
        field: column.id,
        operator: "gte",
        value: zonedStartOfDayUnix(range.from, timezone),
      });
    }
    if (range?.to) {
      filters.push({
        field: column.id,
        operator: "lt",
        value: zonedStartOfDayUnix(range.to, timezone),
      });
    }
    if (filters.length) {
      table.refineCore.setFilters(filters, "merge");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">
          Timezone
        </Label>
        <RemoteSelect<GeodataEditFormType>
          value={geodata}
          triggerType="change"
          showSelected
          className="w-full h-8 text-xs"
          accessorFn={(option) =>
            option
              ? {
                  id: option.id,
                  name: option.timezone
                    ? `${option.name} · ${option.timezone}`
                    : `${option.name} (no timezone)`,
                }
              : undefined
          }
          onSearch={async (query, onStatusChanged) => {
            onStatusChanged("loading");
            const results = await search(query);
            onStatusChanged("success");
            return results.map((item) => geodataEditSchema.parse(item));
          }}
          onValueChanged={(selected) => {
            setGeodata(selected);
            timezoneRef.current = selected?.timezone ?? undefined;
            // Re-apply with the current range so picking (or clearing) the place
            // after the dates still takes effect.
            apply();
          }}
        />
      </div>

      <DatePickerWithRange
        defaultFrom={initialRange?.from ?? new Date()}
        defaultTo={initialRange?.to}
        onDateRangeChanged={(next) => {
          rangeRef.current = next;
          apply();
        }}
      />
    </div>
  );
}
